// RecentPaymentRow — one payment row for the dashboard's "Recent payments"
// card. Owns its own status locally so "Mark paid" can update the badge
// inline without needing the parent list to refetch.
import { useState } from "react";
import { Link } from "react-router-dom";
import MarkPaidControl from "./MarkPaidControl.js";
import ProofSummary from "./ProofSummary.js";
import Badge from "../shared/Badge.js";
import { formatKes } from "../../lib/formatMoney.js";
import type { RecentPayment } from "../../lib/adminDashboardTypes.js";
import type { TransactionStatus } from "@dm-lms/shared/types";

const STATUS_TONE: Record<TransactionStatus, "green" | "gold" | "red" | "gray"> = {
  success:  "green",
  pending:  "gold",
  failed:   "red",
  refunded: "gray",
};

const STATUS_LABEL: Record<TransactionStatus, string> = {
  success:  "Paid",
  pending:  "Pending",
  failed:   "Failed",
  refunded: "Refunded",
};

interface RecentPaymentRowProps {
  payment: RecentPayment;
}

export default function RecentPaymentRow({ payment: initial }: RecentPaymentRowProps) {
  const [payment, setPayment] = useState(initial);
  return (
    <div className="py-2.5">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-sm text-dm-navy">{payment.studentName}</p>
          <p className="truncate text-xs text-dm-slate">{payment.courseTitle}</p>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          <span className="text-sm text-dm-navy">{formatKes(payment.amountKes)}</span>
          <Badge tone={STATUS_TONE[payment.status]}>{STATUS_LABEL[payment.status]}</Badge>
          {payment.status === "pending" && payment.reference === null && (
            <MarkPaidControl payment={payment} onPaid={(settled) => setPayment((prev) => ({ ...prev, ...settled }))} />
          )}
          {payment.status === "pending" && payment.reference !== null && (
            <Link to="/app/admin/payments" className="text-xs font-medium text-dm-royal hover:underline">
              Verify on Payments page
            </Link>
          )}
        </div>
      </div>
      <ProofSummary payment={payment} />
    </div>
  );
}
